export { ASK_USER_APPROVAL_KIND } from './ask-user-details.types';

export const CLINICAL_TASK_APPROVAL_KIND = 'clinical_task';
export const DRAFT_APPROVAL_KIND = 'draft';
export const USER_DISCARDED_DRAFT_REASON = 'user_discarded_draft';

export type DraftPresentation = {
  target: 'consult_note' | 'session_context' | 'session_document';
  body_key: string;
  find_replace_key?: string;
  title_key?: string;
  document_id_key?: string;
  format: 'markdown';
};

export const SESSION_DRAFT_TOOL_NAMES = [
  'edit_consult_note',
  'edit_session_context',
  'create_session_document',
  'edit_session_document',
  'create_session_context_document',
  'update_session_context_document',
] as const;

export type SessionDraftToolName = (typeof SESSION_DRAFT_TOOL_NAMES)[number];

const SESSION_DRAFT_TOOL_NAME_SET: ReadonlySet<string> = new Set(
  SESSION_DRAFT_TOOL_NAMES
);

export const isSessionDraftToolName = (name: string): name is SessionDraftToolName =>
  SESSION_DRAFT_TOOL_NAME_SET.has(name);

/** Used while `tool_input_delta` is still streaming and `context.draft` is not yet on the approval. */
const SESSION_DRAFT_FALLBACK_PRESENTATIONS: Record<
  SessionDraftToolName,
  DraftPresentation
> = {
  edit_consult_note: {
    target: 'consult_note',
    body_key: 'edited_note',
    find_replace_key: 'find_and_replace',
    format: 'markdown',
  },
  edit_session_context: {
    target: 'session_context',
    body_key: 'edited_note',
    find_replace_key: 'find_and_replace',
    format: 'markdown',
  },
  create_session_document: {
    target: 'session_document',
    body_key: 'content',
    title_key: 'name',
    format: 'markdown',
  },
  edit_session_document: {
    target: 'session_document',
    body_key: 'content',
    find_replace_key: 'find_and_replace',
    document_id_key: 'document_id',
    format: 'markdown',
  },
  create_session_context_document: {
    target: 'session_document',
    body_key: 'content',
    title_key: 'name',
    format: 'markdown',
  },
  update_session_context_document: {
    target: 'session_document',
    body_key: 'content',
    title_key: 'name',
    document_id_key: 'context_document_id',
    format: 'markdown',
  },
};

const DRAFT_TARGETS = new Set<DraftPresentation['target']>([
  'consult_note',
  'session_context',
  'session_document',
]);

export const parseDraftPresentation = (value: unknown): DraftPresentation | null => {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  if (
    typeof record.target !== 'string' ||
    !DRAFT_TARGETS.has(record.target as DraftPresentation['target'])
  ) {
    return null;
  }
  if (typeof record.body_key !== 'string' || record.body_key.length === 0) return null;

  const presentation: DraftPresentation = {
    target: record.target as DraftPresentation['target'],
    body_key: record.body_key,
    format: 'markdown',
  };
  if (typeof record.find_replace_key === 'string' && record.find_replace_key) {
    presentation.find_replace_key = record.find_replace_key;
  }
  if (typeof record.title_key === 'string' && record.title_key) {
    presentation.title_key = record.title_key;
  }
  if (typeof record.document_id_key === 'string' && record.document_id_key) {
    presentation.document_id_key = record.document_id_key;
  }
  return presentation;
};

export const resolveDraftPresentation = (
  toolName: string,
  contextDraft?: unknown
): DraftPresentation | null =>
  parseDraftPresentation(contextDraft) ??
  (isSessionDraftToolName(toolName)
    ? SESSION_DRAFT_FALLBACK_PRESENTATIONS[toolName]
    : null);
